/**
 * Products — P4 Master Product Registry list view.
 *
 * Hybrid Elite layout:
 *   - Sticky search + filters header
 *   - Dense but premium product table (SKU, name, category, UoM, supplier count, lead time, compliance)
 *   - Inline "Create product" modal
 *   - Click a row → navigate to /admin/products/:id
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Package, Search, Loader2, Shield, Filter, Boxes, Tag, Trash2, X,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UOM_OPTIONS = ['unit', 'kg', 'ton', 'm', 'm2', 'm3', 'hour', 'licence', 'service'];
const TAX_RULES = ['UAE_VAT_9', 'UAE_ZERO_RATED', 'EXEMPT', 'REVERSE_CHARGE'];
const COMPLIANCE_FLAGS = [
  'hazardous', 'dual_use', 'import_licensed', 'export_licensed',
  'prescription', 'age_restricted', 'food_grade', 'medical_device',
];

const ProductsList = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const token = useMemo(() => localStorage.getItem('admin_token'), []);

  const loadCategories = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/product-categories/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(r.data || []);
    } catch (_) { setCategories([]); }
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/products/`, {
        params: {
          search: search || undefined,
          category_id: categoryFilter || undefined,
          limit: 100,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (_) {
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, token]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  // Debounced search
  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/products/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Product deleted', description: `${deleteTarget.sku} — ${deleteTarget.name}` });
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="products-list-page">
      <div className="mb-4"><BackButton to="/admin/dashboard" label="Dashboard" /></div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Boxes className="w-6 h-6 text-[#023E8A]" />
            <h1 className="text-2xl font-bold text-[#2B2D42]">Master Product Registry</h1>
            <Badge className="bg-[#023E8A] text-white">P4</Badge>
          </div>
          <p className="text-sm text-[#6C757D]">
            Institutional catalogue. Feeds Procurement, Finance, CRM, and Inventory.
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-[#FF6B35] hover:bg-[#E55A28] text-white"
          data-testid="products-create-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New Product
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
              placeholder="Search by SKU, name, or HS code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="products-search-input"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              className="px-2 py-2 border rounded-lg text-sm bg-white"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="products-category-filter"
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="text-xs text-[#6C757D]">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : `${total} products`}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">SKU</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Name</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Category</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">UoM</th>
                <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Cost</th>
                <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Sell</th>
                <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Margin</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">HS</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Suppliers</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Lead</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Compliance</th>
                <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!loading && rows.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-12 text-center text-[#6C757D]">
                  <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  No products yet. Click <b className="text-[#FF6B35]">New Product</b> to add the first one.
                </td></tr>
              )}
              {rows.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/admin/products/${p.id}`)}
                  className="hover:bg-[#023E8A]/5 cursor-pointer"
                  data-testid={`products-row-${p.id}`}
                >
                  <td className="px-4 py-3 font-mono text-[#023E8A] font-semibold">{p.sku}</td>
                  <td className="px-4 py-3 text-[#2B2D42]">
                    <div className="font-medium">{p.name}</div>
                    {p.description && <div className="text-[11px] text-[#6C757D] truncate max-w-md">{p.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-[#6C757D]">
                    {p.category_id && catMap[p.category_id] ? (
                      <Badge variant="outline" className="text-xs"><Tag className="w-3 h-3 mr-1" />{catMap[p.category_id].name}</Badge>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[#6C757D]">{p.unit_of_measure}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#6C757D]">
                    {p.supplier_price != null ? (
                      <span>{p.default_currency || 'AED'} {Number(p.supplier_price).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#023E8A]">
                    {(p.selling_price ?? p.default_price) != null ? (
                      <span>{p.default_currency || 'AED'} {Number(p.selling_price ?? p.default_price).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                    ) : <span className="text-slate-400 font-normal">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid={`product-margin-${p.id}`}>
                    {(() => {
                      const cost = p.supplier_price;
                      const sell = p.selling_price ?? p.default_price;
                      if (cost == null || sell == null || !cost) return <span className="text-slate-400">—</span>;
                      const pct = ((sell - cost) / cost) * 100;
                      const profitable = pct >= 0;
                      return (
                        <span className={profitable ? 'text-[#16A34A] font-semibold' : 'text-red-600 font-semibold'}>
                          {pct.toFixed(1)}%
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-[#6C757D] font-mono text-xs">{p.hs_code || '—'}</td>
                  <td className="px-4 py-3">
                    {(p.default_suppliers || []).length > 0 ? (
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">{p.default_suppliers.length}</Badge>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[#6C757D]">
                    {p.lead_time_days ? `${p.lead_time_days}d` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {(p.compliance_flags || []).length > 0 && (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">
                        <Shield className="w-3 h-3 mr-0.5" />{p.compliance_flags.length}
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(p)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                      title="Delete product"
                      data-testid={`product-delete-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {showCreate && (
        <CreateProductModal
          categories={categories}
          onClose={() => setShowCreate(false)}
          onCreated={(p) => {
            setShowCreate(false);
            toast({ title: 'Product created', description: `${p.sku} — ${p.name}` });
            navigate(`/admin/products/${p.id}`);
          }}
        />
      )}

      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="product-delete-dialog"
        >
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b bg-gradient-to-r from-red-600 to-red-700 text-white flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-80 mb-0.5">Master Product Registry</div>
                <h3 className="text-base font-bold">Delete Product</h3>
              </div>
              <button onClick={() => setDeleteTarget(null)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-[#2B2D42] leading-relaxed">
                This will remove the product from the active registry. References on existing invoices
                will continue to display their frozen snapshot.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6C757D]">SKU</span>
                  <span className="font-mono font-semibold text-[#023E8A]">{deleteTarget.sku}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6C757D]">Name</span>
                  <span className="font-medium text-[#2B2D42] text-right max-w-[60%]">{deleteTarget.name}</span>
                </div>
              </div>
            </div>
            <div className="px-6 py-3 bg-slate-50 border-t flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                data-testid="product-delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="product-delete-confirm-btn"
              >
                {deleting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" />Delete Product</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const CreateProductModal = ({ categories, onClose, onCreated }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    sku: '', name: '', description: '', category_id: '',
    unit_of_measure: 'unit', hs_code: '', lead_time_days: '',
    tax_rule: 'UAE_VAT_9', compliance_flags: [],
    // F-UnifiedProducts — commercial defaults
    product_type: 'goods', default_price: '', default_currency: 'AED',
    default_tax_rate: 5,
    // F-MarginPricing
    supplier_price: '', selling_price: '',
  });
  const [saving, setSaving] = useState(false);

  const toggleFlag = (f) => setForm({
    ...form,
    compliance_flags: form.compliance_flags.includes(f)
      ? form.compliance_flags.filter((x) => x !== f)
      : [...form.compliance_flags, f],
  });

  const submit = async () => {
    if (!form.sku.trim() || !form.name.trim()) {
      toast({ title: 'SKU and Name required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const t = localStorage.getItem('admin_token');
      const payload = {
        ...form,
        lead_time_days: form.lead_time_days ? parseInt(form.lead_time_days, 10) : null,
        default_price: form.default_price === '' ? (form.selling_price === '' ? null : parseFloat(form.selling_price)) : parseFloat(form.default_price),
        default_tax_rate: parseFloat(form.default_tax_rate) || 0,
        supplier_price: form.supplier_price === '' ? null : parseFloat(form.supplier_price),
        selling_price: form.selling_price === '' ? null : parseFloat(form.selling_price),
      };
      const r = await axios.post(`${API_URL}/api/products/`, payload, {
        headers: { Authorization: `Bearer ${t}` },
      });
      onCreated(r.data);
    } catch (err) {
      toast({
        title: 'Create failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="products-create-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b">
          <h3 className="text-lg font-bold text-[#2B2D42]">Create Product</h3>
          <p className="text-xs text-[#6C757D]">Add a new institutional SKU to the Master Registry.</p>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#6C757D] uppercase">SKU *</label>
              <input className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-mono"
                     placeholder="IND-PMP-3HP"
                     value={form.sku}
                     onChange={(e) => setForm({ ...form, sku: e.target.value })}
                     data-testid="products-create-sku" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6C757D] uppercase">Name *</label>
              <input className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                     placeholder="Industrial Centrifugal Pump 3HP"
                     value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="products-create-name" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6C757D] uppercase">Description</label>
            <textarea rows={2} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#6C757D] uppercase">Category</label>
              <select className="mt-1 w-full px-2 py-2 border rounded-lg text-sm"
                      value={form.category_id}
                      onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                <option value="">—</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6C757D] uppercase">Unit of Measure</label>
              <select className="mt-1 w-full px-2 py-2 border rounded-lg text-sm"
                      value={form.unit_of_measure}
                      onChange={(e) => setForm({ ...form, unit_of_measure: e.target.value })}>
                {UOM_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6C757D] uppercase">Lead Time (days)</label>
              <input type="number" className="mt-1 w-full px-2 py-2 border rounded-lg text-sm"
                     value={form.lead_time_days}
                     onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#6C757D] uppercase">HS Code</label>
              <input className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-mono"
                     placeholder="8413.70.90"
                     value={form.hs_code}
                     onChange={(e) => setForm({ ...form, hs_code: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6C757D] uppercase">Tax Rule</label>
              <select className="mt-1 w-full px-2 py-2 border rounded-lg text-sm"
                      value={form.tax_rule}
                      onChange={(e) => setForm({ ...form, tax_rule: e.target.value })}>
                {TAX_RULES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6C757D] uppercase">Compliance Flags</label>
            <div className="mt-1 flex flex-wrap gap-1">
              {COMPLIANCE_FLAGS.map((f) => (
                <button key={f} type="button" onClick={() => toggleFlag(f)}
                        className={`text-xs px-2 py-1 border rounded ${form.compliance_flags.includes(f) ? 'bg-amber-100 border-amber-400 text-amber-800' : 'bg-white border-slate-200 text-[#6C757D]'}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* F-UnifiedProducts — Commercial defaults (feeds Invoices, Quotations, POs) */}
          <div className="pt-3 mt-2 border-t border-slate-200">
            <h4 className="text-xs font-bold text-[#023E8A] uppercase tracking-wide mb-2">
              Commercial defaults &amp; margin
            </h4>
            <p className="text-[11px] text-[#6C757D] mb-3 leading-snug">
              Supplier price = your cost. Selling price = what we charge the
              customer. Both auto-fill the line items and feed margin reports.
              All values remain editable at document level.
            </p>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#6C757D] uppercase">Type</label>
                <select className="mt-1 w-full px-2 py-2 border rounded-lg text-sm"
                        value={form.product_type}
                        onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                        data-testid="products-create-type">
                  <option value="goods">Goods</option>
                  <option value="service">Service</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6C757D] uppercase">Currency</label>
                <select className="mt-1 w-full px-2 py-2 border rounded-lg text-sm"
                        value={form.default_currency}
                        onChange={(e) => setForm({ ...form, default_currency: e.target.value })}
                        data-testid="products-create-default-currency">
                  <option value="AED">AED</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6C757D] uppercase">Tax Rate %</label>
                <input type="number" step="0.5" min="0" max="100"
                       className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                       value={form.default_tax_rate}
                       onChange={(e) => setForm({ ...form, default_tax_rate: e.target.value })}
                       data-testid="products-create-default-tax" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6C757D] uppercase">Lead Time (days)</label>
                <input type="number" min="0"
                       className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                       placeholder="—"
                       value={form.lead_time_days}
                       onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <label className="text-xs font-semibold text-[#6C757D] uppercase">
                  Supplier Price <span className="text-slate-400 normal-case">(cost)</span>
                </label>
                <input type="number" step="0.01" min="0"
                       className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                       placeholder="0.00"
                       value={form.supplier_price}
                       onChange={(e) => setForm({ ...form, supplier_price: e.target.value })}
                       data-testid="products-create-supplier-price" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6C757D] uppercase">
                  Selling Price <span className="text-[#16A34A] normal-case">(our price)</span>
                </label>
                <input type="number" step="0.01" min="0"
                       className="mt-1 w-full px-3 py-2 border rounded-lg text-sm border-[#16A34A]/30"
                       placeholder="0.00"
                       value={form.selling_price}
                       onChange={(e) => setForm({ ...form, selling_price: e.target.value })}
                       data-testid="products-create-selling-price" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6C757D] uppercase">Margin</label>
                <div className="mt-1 px-3 py-2 border rounded-lg text-sm bg-slate-50 tabular-nums"
                     data-testid="products-create-margin-preview">
                  {(() => {
                    const sp = parseFloat(form.supplier_price);
                    const lp = parseFloat(form.selling_price);
                    if (!sp || !lp) return <span className="text-slate-400">—</span>;
                    const abs = (lp - sp).toFixed(2);
                    const pct = ((lp - sp) / sp * 100).toFixed(1);
                    const profitable = lp >= sp;
                    return (
                      <span className={profitable ? 'text-[#16A34A] font-semibold' : 'text-red-600 font-semibold'}>
                        {form.default_currency} {abs} <span className="text-xs font-normal">({pct}%)</span>
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#023E8A] text-white"
                  data-testid="products-create-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Create
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductsList;
